import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const LOGO_URL = process.env.REACT_APP_LOGO_URL;

const NAV_ITEMS = [
  { label: "About", href: "#about" },
  { label: "Programs", href: "#programs" },
  { label: "Why Us", href: "#why-choose" },
  { label: "Safety", href: "#safety" },
  { label: "Partners", href: "#institutions" },
  { label: "Blog", href: "/blog", isRoute: true },
  { label: "Contact", href: "#inquiry" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleNavClick = (e, item) => {
    if (item.isRoute) return;
    e.preventDefault();
    setMobileOpen(false);
    if (!isHome) {
      window.location.href = "/" + item.href;
      return;
    }
    const el = document.querySelector(item.href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-border/40 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
          <img src={LOGO_URL} alt="OSAA Logo" className="h-10 md:h-12 w-auto" />
          <div className="flex flex-col">
            <span className={`font-body text-sm md:text-base font-semibold tracking-tight leading-tight ${scrolled ? 'text-foreground' : 'text-white'}`}>
              Open Sky
            </span>
            <span className={`font-body text-[10px] tracking-[0.12em] uppercase ${scrolled ? 'text-accent' : 'text-white/70'}`}>
              Adventure Academy
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {NAV_ITEMS.map((item) =>
            item.isRoute ? (
              <Link key={item.label} to={item.href} className={`text-sm font-medium tracking-wide transition-colors hover:text-accent ${scrolled ? "text-foreground/70" : "text-white/80 hover:text-white"}`}>
                {item.label}
              </Link>
            ) : (
              <a key={item.label} href={item.href} onClick={(e) => handleNavClick(e, item)} className={`text-sm font-medium tracking-wide transition-colors hover:text-accent ${scrolled ? "text-foreground/70" : "text-white/80 hover:text-white"}`}>
                {item.label}
              </a>
            )
          )}
          <a href="tel:+918618396055" data-testid="header-phone-btn" className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-sm transition-all ${scrolled ? "bg-accent text-accent-foreground hover:bg-accent/90" : "bg-white/15 text-white border border-white/30 hover:bg-white/25"}`}>
            <Phone className="w-3.5 h-3.5" />
            +91 8618396055
          </a>
        </nav>

        <button data-testid="mobile-menu-toggle" className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X className={`w-6 h-6 ${scrolled ? "text-foreground" : "text-white"}`} /> : <Menu className={`w-6 h-6 ${scrolled ? "text-foreground" : "text-white"}`} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-b border-border/40" data-testid="mobile-menu">
          <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            {NAV_ITEMS.map((item) =>
              item.isRoute ? (
                <Link key={item.label} to={item.href} onClick={() => setMobileOpen(false)} className="text-base font-medium text-foreground/80 hover:text-accent transition-colors">{item.label}</Link>
              ) : (
                <a key={item.label} href={item.href} onClick={(e) => handleNavClick(e, item)} className="text-base font-medium text-foreground/80 hover:text-accent transition-colors">{item.label}</a>
              )
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
